import {FormControlLabel, Switch} from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import React, {useCallback, useEffect, useState} from "react";
import {ABWASSERBEHANDLUNG, ENTLASTUNG, KANALNETZ, KUEHLWASSER} from "../../consts/anlagentyp";
import {AUSSERHALB_RLP, INDUSTRIE, KANAL_INDUSTRIE, KANAL_KOMMUNAL, KOMMUNAL, PRIVAT} from "../../consts/betriebstyp";
import {FACHOBJEKTSTATUS_IN_BETRIEB, FACHOBJEKTSTATUS_STILLGELEGT, isFinalStatus} from "../../consts/fachobjektstatus";
import {
    BEAUFTRAGTER,
    BETREIBER,
    FACHADMIN,
    SYSTEMADMIN,
    ZUST_SBB,
    ZUST_SBD,
    ZUST_SBS
} from "../../consts/funktionaleRollen";
import {
    ABLAUF_KUEHLWASSER,
    ABLAUF_NACHKLAERUNG,
    ABLAUF_VORKLAERUNG,
    EINLEITSTELLE_BECKENUEBERLAUF,
    EINLEITSTELLE_BODENFILTER,
    EINLEITSTELLE_GRUNDWASSER,
    EINLEITSTELLE_KLAERUEBERLAUF,
    EINLEITSTELLE_MISCHWASSERANLAGE,
    EINLEITSTELLE_NIEDERSCHLAGSWASSERANLAGE, EINLEITSTELLE_NOTENTLASTUNG, EINLEITSTELLE_REGENRUECKHALTEBECKEN,
    ENDKONTROLLMESSSTELLE,
    messstellentypIdToObj,
    NOTABLAUF_VORKLAERUNG,
    SONDE_ABLAUF,
    SONDE_BECKEN,
    SONDE_BECKENUEBERLAUF,
    SONDE_KLAERUEBERLAUF,
    SONDE_PUMPWERK,
    SONSTIGE,
    ZULAUF,
    ZULAUF_1_BIOL_STUFE,
    ZULAUF_2_BIOL_STUFE,
} from "../../consts/messstellentyp";
import routes from "../../consts/routes.json";
import {buildBescheidTitle} from "../../initialObjects/BescheidObjects";
import "../../styles/components/betriebsakteNavigation.css";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EASelectButton from "../EASelectButton";
import EASlide from "../EASlide";
import EATree from "../tree/EATree";
import {EATreeItem} from "../tree/EATreeItem";
import {useHttp} from "../../services/HttpService";
import {getBescheidQualitaetEndpoint} from "../../services/WertelistenService";
import {
    getEntlastungUebersichtEndpoint,
    getSuevoaEnabledEndpoint
} from "../../services/AnlagenService";

const anlageButtonAbwasserbehandlung = "Abwasserbehandlungsanlage";
const anlageButtonKleinklaer = "Kleinkläranlage";
const anlageButtonVorbehandlung = "Vorbehandlungsanlage";
const anlageButtonKuehlwasser = "Kühlwasseranlage";
const anlageButtonAusserhalb = "Abwasserbehandlungsanlage (außerhalb)";
const anlageButtonMischwasser = "Mischwasseranlage";
const anlageButtonNiederschlag = "Niederschlagswasseranlage";
const anlageButtonKanal = "Kanalnetz";

const id = "BetriebsakteNavigation";

/**
 * Komponente, welche die Baumstruktur der Betriebsakte widerspiegelt.
 * @param betriebstyp Der Betriebstyp
 * @param navObject
 * @param loading Gibt an, ob die darzustellende Navigation aktuell geladen wird
 * @param modus Gibt an, ob Schaltflächen angezeigt werden sollen (==="s")
 * @param location
 * @param match
 * @param history
 * @param isHierarchischStillgelegt
 * @param setIsHierarchischStillgelegt
 * @returns {JSX.Element}
 * @constructor
 */
function BetriebsakteNavigation({
                                    betriebsId,
                                    betriebstyp,
                                    navObject,
                                    loading,
                                    modus,
                                    location,
                                    match,
                                    history,
                                    setIsHierarchischStillgelegt,
                                    jahresberichtData,
                                    jahresberichtAnlagen
                                }) {
    const [bescheidQualitaetOptionen] = useHttp(getBescheidQualitaetEndpoint);

    const {id: betrIdParam} = match.params;
    const [isCreateAllowed, setIsCreateAllowed] = useState(false);
    const confirm = useConfirmation();

    const [treeNav, setTreeNav] = useState({});
    const [navOpen, setNavOpen] = useState(true);

    const [expanded, setExpanded] = React.useState([]);
    const [selected, setSelected] = React.useState([]);

    const [{
        data: entlastungData,
        loading: entlastungDataLoading
    }] = useHttp(getEntlastungUebersichtEndpoint(getBetriebsId()));

    const [{
        data: suevoaEnabled,
        loading: suevoaEnabledLoading
    }] = useHttp(getSuevoaEnabledEndpoint());

    function getBetriebsId() {
        if (betriebsId) {
            return betriebsId;
        }
        return 0;
    }

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const expandTree = useCallback(
        (path) => {
            const tmp = [...expanded];
            let i = path;
            while (i) {
                if (!tmp.includes(i)) {
                    tmp.push(i);
                }
                i = i.replace(/\/[^/]*$/, "");
            }
            if (selected !== path) {
                setExpanded(tmp);
                setSelected(path);
            }
        },
        [expanded, selected]
    );

    const extractIdFromStartOfUrlPath = useCallback((urlPath) => parseInt(urlPath.match(/^\d+/)[0]), []);

    const findFachobjekt = useCallback(
        (parent, fachobjektTitle, fachobjektId) => {
            if (!parent) {
                return null;
            }
            return parent[fachobjektTitle]?.find((f) => f.id === fachobjektId);
        },
        []);

    const checkInnerFachobjektForStillgelegt = useCallback(
        (fachobkjekt, innerObjektId, innerObjektTitle) => {
            const innerObjekt = findFachobjekt(fachobkjekt, innerObjektTitle, innerObjektId);
            if (isFinalStatus(innerObjekt?.status.id)) {
                setIsHierarchischStillgelegt(true);
                return true;
            }
            return false;
        },
        [setIsHierarchischStillgelegt, findFachobjekt]
    );

    const checkMessstelleForStillgelegt = useCallback(
        (path, anlage) => {
            if (path.startsWith(routes.BETRIEBSAKTE_MESSSTELLEN_PART) && !path.includes("uebersicht")) {
                const messstellenId = extractIdFromStartOfUrlPath(path.substring(routes.BETRIEBSAKTE_MESSSTELLEN_PART.length));
                return checkInnerFachobjektForStillgelegt(anlage, messstellenId, "messstellen");
            }
            return false;
        },
        [extractIdFromStartOfUrlPath, checkInnerFachobjektForStillgelegt]
    );

    const checkInnerAnlageForStillgelegt = useCallback(
        (path, anlage, anlagentyp, anlagenroute) => {
            if (path.startsWith(anlagenroute) && !path.includes("/entlastung/uebersicht") && !path.includes("/kanalnetz/uebersicht")) {
                const pathWithoutTyp = path.substring(anlagenroute.length);
                const anlagenId = extractIdFromStartOfUrlPath(pathWithoutTyp);
                if (!checkInnerFachobjektForStillgelegt(anlage, anlagenId, anlagentyp)) {
                    const innerAnlage = findFachobjekt(anlage, anlagentyp, anlagenId);
                    return checkMessstelleForStillgelegt(pathWithoutTyp.replace(/^\d+/, ""), innerAnlage);
                } else {
                    return true;
                }
            }
            return false;
        },
        [checkMessstelleForStillgelegt, checkInnerFachobjektForStillgelegt, extractIdFromStartOfUrlPath, findFachobjekt]
    );


    /**
     * Callback, welcher prüft, ob ein Fachobjekt bearbeitet werden darf. Hierbei wird hierarchisch vom Betrieb ausgehend geprüft. Die Rechte sollten auf der Fachobjektmaske geprüft werden
     * @type {function(*, *=): undefined}
     */
    const checkHierarchischForStillgelegt = useCallback(
        (path, tree) => {
            if (path.includes("neu") || !tree) {
                setIsHierarchischStillgelegt(false);
                return;
            }

            if (isFinalStatus(tree.status.id)) {
                setIsHierarchischStillgelegt(true);
                return;
            }

            const pathWithoutBetrieb = path.replace(/^(\/[^/]*){2}\//, ""); //zwei mal "/" gefolgt von irgendwas, gefolgt von / -> /betriebsakte/betriebsId/
            if (/^\d/.test(pathWithoutBetrieb)) {
                const anlageId = extractIdFromStartOfUrlPath(pathWithoutBetrieb);
                const anlage = findFachobjekt(tree, "anlagen", anlageId);
                if (checkInnerFachobjektForStillgelegt(tree, anlageId, "anlagen")) {
                    return;
                }

                const pathWithoutAnlage = pathWithoutBetrieb.replace(/^\d+/, "");
                if (checkMessstelleForStillgelegt(pathWithoutAnlage, anlage) ||
                    checkInnerAnlageForStillgelegt(pathWithoutAnlage, anlage, "entlastungsanlagen", routes.BETRIEBSAKTE_ENTLASTUNG_PART) ||
                    checkInnerAnlageForStillgelegt(pathWithoutAnlage, anlage, "kanalnetz", routes.BETRIEBSAKTE_KANAL_PART)) {
                    return;
                }
            }

            setIsHierarchischStillgelegt(false);
        },
        [
            setIsHierarchischStillgelegt,
            checkMessstelleForStillgelegt,
            checkInnerFachobjektForStillgelegt,
            checkInnerAnlageForStillgelegt,
            extractIdFromStartOfUrlPath,
            findFachobjekt,
        ]
    );

    useEffect(() => {
        if (betrIdParam) {
            expandTree(location.pathname);
            setTreeNav(navObject);
            checkHierarchischForStillgelegt(location.pathname, navObject);
        }
    }, [location, betrIdParam, navObject, expandTree, checkHierarchischForStillgelegt]);

    const createSelectButton = (anlageButtonId, options, url, buildParams, label = "Anlage anlegen", createUrl = undefined) => {
        if (!isCreateAllowed || !userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN])) {
            return null;
        }
        return (
            <EASelectButton
                label={label}
                id={anlageButtonId + "-AnlageButton"}
                options={options}
                {...setStyle(url, createUrl)}
                onItemClick={(i) => navigateTo(buildCreateUrl(url, buildParams(i), createUrl))}
            />
        );
    };

    function setStyle(url, createUrl = "/neu/anlagendaten") {
        const opts = {};
        if (location.pathname === url + createUrl) {
            opts.style = {backgroundColor: "#CBDDF8"};
        }

        return opts;
    }

    function createSelectMessstelleButton(parentAnlage, nodeUrl) {
        const options = [];
        if (parentAnlage.typ === ENTLASTUNG) {
            options.push(
                SONDE_BECKEN,
                SONDE_BECKENUEBERLAUF,
                SONDE_KLAERUEBERLAUF,
                SONDE_ABLAUF,
                SONDE_PUMPWERK,
                EINLEITSTELLE_MISCHWASSERANLAGE,
                EINLEITSTELLE_NIEDERSCHLAGSWASSERANLAGE,
                EINLEITSTELLE_BECKENUEBERLAUF,
                EINLEITSTELLE_BODENFILTER,
                EINLEITSTELLE_GRUNDWASSER,
                EINLEITSTELLE_KLAERUEBERLAUF,
                EINLEITSTELLE_NOTENTLASTUNG,
                EINLEITSTELLE_REGENRUECKHALTEBECKEN
            );
        } else {
            options.push(
                ZULAUF,
                NOTABLAUF_VORKLAERUNG,
                ABLAUF_VORKLAERUNG,
                ZULAUF_1_BIOL_STUFE,
                ZULAUF_2_BIOL_STUFE,
                ABLAUF_KUEHLWASSER,
                ABLAUF_NACHKLAERUNG,
                ENDKONTROLLMESSSTELLE,
                SONSTIGE
            );
        }
        return createSelectButton(
            "messstelle-AnlegeButton",
            options.map((o) => mLabel(o)),
            nodeUrl,
            (i) => {
                if (options.length > i) {
                    return "typ=" + options[i];
                }
                return "";
            },
            "Messstelle anlegen",
            "/neu/messstellendaten"
        );
    }

    function createSelectBescheidButton(nodeUrl) {
        const createUrl = "/neu/bescheiddaten";
        if (!isCreateAllowed || !userHasAnyRole([ZUST_SBB, FACHADMIN, SYSTEMADMIN])) {
            return null;
        }

        const items = bescheidQualitaetOptionen.loading ? [] : bescheidQualitaetOptionen.data;

        return createSelectButton(
            "bescheid-AnlegeButton",
            items.map(item => item.value),
            nodeUrl,
            (i) => {
                if (items.length > i) {
                    return "qualitaet=" + items[i].id;
                }
                return "";
            },
            "Bescheid anlegen",
            createUrl
        );
    }

    function createBescheidAntragButton(nodeUrl) {
        const createUrl = "/neu/bescheiddaten";
        if (isCreateAllowed && userHasAnyRole([BETREIBER, BEAUFTRAGTER])) {
            return (
                <EAButton
                    id={"antragAnlageButton"}
                    {...setStyle(nodeUrl, createUrl)}
                    label={"Antrag anlegen"}
                    onClick={() => navigateTo(buildCreateUrl(nodeUrl, "", createUrl))}
                />
            );
        }
    }

    const createAnlageButton = (label, url, param) => {
        if (!isCreateAllowed || !userHasAnyRole([ZUST_SBD, FACHADMIN, SYSTEMADMIN])) {
            return null;
        }

        const createUrl = buildCreateUrl(url, param);
        return (
            <EAButton id={id + "-rootAnlageButton"} {...setStyle(url)} label={label + " anlegen"} onClick={() => navigateTo(createUrl)}/>
        );
    };

    function buildCreateRootAnlageButton(url) {
        if (!isCreateAllowed || url.startsWith(routes.BETRIEBSAKTE_NEW)) {
            return null;
        }

        switch (betriebstyp.id) {
            case KOMMUNAL:
                return createSelectButton(
                    "kommunal",
                    [anlageButtonAbwasserbehandlung, anlageButtonKleinklaer, anlageButtonVorbehandlung, anlageButtonKuehlwasser],
                    url,
                    (i) => {
                        switch (i) {
                            case 0:
                                return "typ=ABWASSERBEHANDLUNG&art=ABWASSERBEHANDLUNG";
                            case 1:
                                return "typ=ABWASSERBEHANDLUNG&art=KLEINKLAERANLAGE";
                            case 2:
                                return "typ=ABWASSERBEHANDLUNG&art=ABWASSERVORBEHANDLUNG";
                            case 3:
                                return "typ=KUEHLWASSER";
                            default:
                                return "";
                        }
                    }
                );

            case INDUSTRIE:
                return createSelectButton(
                    "industrie",
                    [anlageButtonAbwasserbehandlung, anlageButtonVorbehandlung, anlageButtonKuehlwasser],
                    url,
                    (i) => {
                        switch (i) {
                            case 0:
                                return "typ=ABWASSERBEHANDLUNG&art=ABWASSERBEHANDLUNG";
                            case 1:
                                return "typ=ABWASSERBEHANDLUNG&art=ABWASSERVORBEHANDLUNG";
                            case 2:
                                return "typ=KUEHLWASSER";
                            default:
                                return "";
                        }
                    }
                );

            case PRIVAT:
                return createSelectButton(
                    "privat",
                    [anlageButtonAbwasserbehandlung, anlageButtonKleinklaer],
                    url,
                    (i) => {
                        switch (i) {
                            case 0:
                                return "typ=ABWASSERBEHANDLUNG&art=ABWASSERBEHANDLUNG";
                            case 1:
                                return "typ=ABWASSERBEHANDLUNG&art=KLEINKLAERANLAGE";
                            default:
                                return "";
                        }
                    }
                );

            case AUSSERHALB_RLP:
                return createAnlageButton(anlageButtonAusserhalb, url, "typ=ABWASSERBEHANDLUNG&art=ANLAGE_AUSSERHALB");

            case KANAL_INDUSTRIE:
            case KANAL_KOMMUNAL:
                return createSelectButton(
                    "kanalKommunal",
                    [anlageButtonMischwasser, anlageButtonNiederschlag, anlageButtonKanal],
                    url,
                    (i) => {
                        switch (i) {
                            case 0:
                                return "typ=ENTLASTUNG&art=MISCHWASSER";
                            case 1:
                                return "typ=ENTLASTUNG&art=NIEDERSCHLAGSWASSER";
                            case 2:
                                return "typ=KANALNETZ";
                            default:
                                return "";
                        }
                    }
                );
            default:
                return null;
        }
    }

    function buildTreeNavigation() {
        const nodeUrl = routes.BETRIEBSAKTE_RAW + "/" + betrIdParam;
        return (
            <EATree expanded={expanded} selected={selected} onNodeToggle={handleToggle}>
                {buildNavigateToTreeItem("Betriebsdaten", nodeUrl + routes.BETRIEBSAKTE_BETRIEBSDATEN)}
                {!isNaN(betrIdParam) && buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {!isFinalStatus(treeNav?.status?.id) && buildCreateRootAnlageButton(nodeUrl)}
                {mapChildren(treeNav, (n) => buildInnerTree(n, nodeUrl), "anlagen")}
                {!entlastungDataLoading && entlastungData && buildEntlastungsTree(nodeUrl, entlastungData.length)}
                {treeNav?.freigabestatus === "FREIGEGEBEN" && buildBescheidTree(treeNav, nodeUrl)}
                {jahresberichtData && !suevoaEnabledLoading && suevoaEnabled && buildJahresberichteTree(nodeUrl, jahresberichtData.length)}
            </EATree>
        );
    }

    function buildInnerTree(innerTree, parentUrl) {
        switch (innerTree.typ) {
            case ABWASSERBEHANDLUNG:
                return buildAbwasserbehandlungNode(innerTree, parentUrl);

            case KUEHLWASSER:
                return buildKuehlwasserNode(innerTree, parentUrl);

            case KANALNETZ:
                return buildKanalNode(innerTree, parentUrl);

            default:
                return null;
        }
    }

    const totalNumOfElementInInnerTree = (innerTree, elementName) => {
        return innerTree[elementName] ? innerTree[elementName].length : 0;
    };

    function buildAbwasserbehandlungNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;
        const totalNumOfKanalanlagen = totalNumOfElementInInnerTree(innerTree, "kanalnetz");
        const totalNumOfMessstellen = totalNumOfElementInInnerTree(innerTree, "messstellen");

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={innerTree.title}>
                {buildNavigateToTreeItem("Anlagendaten", nodeUrl + routes.BETRIEBSAKTE_ANLAGENDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {innerTree.hasIndirekteinleiter
                    ? buildNavigateToTreeItem("Indirekteinleiter", nodeUrl + routes.BETRIEBSAKTE_INDIREKTEINLEITER)
                    : null}
                {buildMessstellenTree(innerTree, nodeUrl, totalNumOfMessstellen)}
                {buildKanalTree(innerTree, nodeUrl, totalNumOfKanalanlagen)}
            </EATreeItem>
        );
    }

    function buildKuehlwasserNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;
        const totalNumOfMessstellen = totalNumOfElementInInnerTree(innerTree, "messstellen");

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={innerTree.title}>
                {buildNavigateToTreeItem("Anlagendaten", nodeUrl + routes.BETRIEBSAKTE_ANLAGENDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {buildMessstellenTree(innerTree, nodeUrl, totalNumOfMessstellen)}
            </EATreeItem>
        );
    }

    function buildEntlastungsTree(parentUrl, totalNum) {
        const nodeUrl = parentUrl + "/" + ENTLASTUNG.toLowerCase();
        const selectedEntlastungItem = mapChildren(treeNav, (n) => buildEntlastungNode(n, nodeUrl), "anlagen").filter(item => location.pathname.includes(item.key));

        return (
            <EATreeItem key={nodeUrl} nodeId={"" + nodeUrl} label={"Misch-/Niederschlagswasser"}>
                {buildNavigateToTreeItem("Übersicht (" + totalNum + ")", parentUrl + "/entlastung/uebersicht/")}
                {createSelectButton("innerEntlastung", [anlageButtonMischwasser, anlageButtonNiederschlag], nodeUrl, (i) => {
                    switch (i) {
                        case 0:
                            return "typ=ENTLASTUNG&art=MISCHWASSER";

                        case 1:
                            return "typ=ENTLASTUNG&art=NIEDERSCHLAGSWASSER";

                        default:
                            return "";
                    }
                })}
                {selectedEntlastungItem && selectedEntlastungItem.length === 1 && selectedEntlastungItem[0]}
            </EATreeItem>
        );
    }

    function buildEntlastungNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;
        const totalNumOfMessstellen = totalNumOfElementInInnerTree(innerTree, "messstellen");

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={innerTree.title}>
                {buildNavigateToTreeItem("Anlagendaten", nodeUrl + routes.BETRIEBSAKTE_ANLAGENDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {buildMessstellenTree(innerTree, nodeUrl, totalNumOfMessstellen)}
            </EATreeItem>
        );
    }

    function buildJahresberichteTree(parentUrl, totalNum) {
        const nodeUrl = parentUrl + "/jahresbericht";

        return (
            <EATreeItem key={nodeUrl} nodeId={"" + nodeUrl} label={"Jahresberichte Abwasserbehandlung"}>
                {buildNavigateToTreeItem("Übersicht (" + totalNum + ")", parentUrl + "/jahresbericht/uebersicht/")}
                {createSelectJahresberichtButton(nodeUrl)}
                {jahresberichtAnlagen && mapChildren(jahresberichtAnlagen, (n) => buildJahresberichtNode(n, nodeUrl))}
            </EATreeItem>
        );
    }

    function buildJahresberichtNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" ;
        const berichte = getBerichteForAnlage(innerTree.id);

        return (
            <EATreeItem nodeId={nodeUrl + innerTree.id} label={innerTree.bezeichnung}>
                {berichte && mapChildren(berichte, (n) => buildJahresberichtJahrNode(n, nodeUrl))}
            </EATreeItem>
        );
    }

    function buildJahresberichtJahrNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + innerTree.id;
        const berichtsjahr = new Date(innerTree.von).getFullYear()

        return (
            <EATreeItem key={nodeUrl} nodeId={"jahresbericht-subnode-" + nodeUrl} label={new Date(innerTree.von).getFullYear()}>
                {buildNavigateToTreeItem("Jahresdaten", nodeUrl + routes.JAHRESBERICHTDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {buildMesswerteNode(innerTree, nodeUrl, berichtsjahr)}
            </EATreeItem>
        );
    }

    function isMessstelleValidForJahresbericht(messstelle, berichtsjahr) {
        if (messstelle.status.id === FACHOBJEKTSTATUS_IN_BETRIEB) {
            return true;
        }
        else if (messstelle.status.id === FACHOBJEKTSTATUS_STILLGELEGT && new Date(messstelle.date).getFullYear() >= berichtsjahr) {
            return true;
        }
        return false;
    }

    function buildMesswerteNode(innerTree, parentUrl, berichtsjahr) {
        const nodeUrl = parentUrl + "/messstelle/";
        const anlageId = innerTree.anlage.id;
        const messstellen = treeNav.anlagen.find(element => element.id === anlageId).messstellen;
        const filteredMessstellen = messstellen.filter(messstelle => isMessstelleValidForJahresbericht(messstelle, berichtsjahr));

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={"Messwerte"}>
                {filteredMessstellen && mapChildren(filteredMessstellen, (n) => buildJahresberichtMessstelleNode(n, nodeUrl))}
            </EATreeItem>
        );
    }


    function buildJahresberichtMessstelleNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + innerTree.id;

        return (
            buildNavigateToTreeItem(innerTree.title, nodeUrl)
        );
    }


    function getBerichteForAnlage(anlageID) {
        let berichte = [];
        jahresberichtData.forEach((bericht) => {
            if (bericht.anlage.id === anlageID) {
                berichte.push(bericht);
            }
        });
        return berichte;
    }

    function createSelectJahresberichtButton(nodeUrl) {
        let createUrl = "/neu/jahresberichtdaten";
        if (!isCreateAllowed || !userHasAnyRole([BETREIBER, ZUST_SBS, FACHADMIN, SYSTEMADMIN])) {
            return null;
        }
        return <EAButton
            id={"jahresbericht-AnlageButton"}
            {...setStyle(nodeUrl, createUrl)}
            label={"Jahresbericht anlegen"}
            onClick={() => navigateTo(buildCreateUrl(nodeUrl, "", createUrl))}
        />;
    }

    function buildKanalTree(parentAnlage, parentUrl, totalNum) {
        const nodeUrl = parentUrl + "/" + KANALNETZ.toLowerCase();
        const hasKanalnetz = hasChildren(parentAnlage, "kanalnetz");
        let selectedKanalnetzItem = mapChildren(parentAnlage, (n) => buildKanalNode(n, nodeUrl), "kanalnetz");

        if (selectedKanalnetzItem) {
            selectedKanalnetzItem = selectedKanalnetzItem.filter(item => location.pathname.includes(item.key))
        }

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={"Kanalnetze"}>
                {hasKanalnetz && buildNavigateToTreeItem("Übersicht (" + totalNum + ")", nodeUrl + "/uebersicht/" + parentAnlage.id)}
                {!isFinalStatus(parentAnlage?.status?.id) &&
                    createAnlageButton(anlageButtonKanal, nodeUrl, "typ=KANALNETZ")}
                {selectedKanalnetzItem && selectedKanalnetzItem.length === 1 && selectedKanalnetzItem[0]}
            </EATreeItem>
        );
    }

    function buildKanalNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;
        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={innerTree.title}>
                {buildNavigateToTreeItem("Anlagendaten", nodeUrl + routes.BETRIEBSAKTE_ANLAGENDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
            </EATreeItem>
        );
    }

    const mLabel = (mId) => {
        return messstellentypIdToObj(mId).value;
    };

    function buildMessstellenTree(parentAnlage, parentUrl, totalNum) {
        const nodeUrl = parentUrl + "/messstellen";
        const hasMessstellen = hasChildren(parentAnlage, "messstellen");

        let selectedMessstellenItem = mapChildren(parentAnlage, (n) => buildMessstelleNode(n, nodeUrl), "messstellen");

        if (selectedMessstellenItem) {
            selectedMessstellenItem = selectedMessstellenItem.filter(item => location.pathname.includes(item.key))
        }

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={"Messstellen"}>
                {hasMessstellen && buildNavigateToTreeItem("Übersicht (" + totalNum + ")", nodeUrl + "/uebersicht")}
                {!isFinalStatus(parentAnlage?.status?.id) && createSelectMessstelleButton(parentAnlage, nodeUrl)}
                {selectedMessstellenItem && selectedMessstellenItem.length === 1 && selectedMessstellenItem[0]}
            </EATreeItem>
        );
    }

    function buildMessstelleNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;

        const url = nodeUrl + routes.BETRIEBSAKTE_MESSSTELLENDATEN;

        const opts = {};
        if (url) {
            opts.onLabelClick = () => navigateTo(url + "?modus=l");
        }

        return (
            <EATreeItem key={url} nodeId={url} label={innerTree.title} {...opts} >
            </EATreeItem>
        );
    }

    function buildBescheidTree(parent, parentUrl) {
        const nodeUrl = parentUrl + "/bescheid";
        const totalNumOfBescheide = totalNumOfElementInInnerTree(parent, "bescheide");
        const selectedBescheidItem = mapChildren(parent, (n) => buildBescheidNode(n, nodeUrl), "bescheide")
            .filter(item => location.pathname.includes(item.key + "/")); // Bescheid-URLs haben anderen Aufbau, daher muss am Ende noch "/" angefügt werden

        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={"Bescheide"}>
                {!isFinalStatus(parent?.status?.id) && createSelectBescheidButton(nodeUrl)}
                {buildNavigateToTreeItem("Übersicht Bescheide/Erklärungen (" + totalNumOfBescheide + ")", parentUrl + "/bescheiduebersicht")}
                {buildNavigateToTreeItem("Übersicht Parameterwerte", parentUrl + "/parameteruebersicht")}
                {selectedBescheidItem && selectedBescheidItem.length === 1 && selectedBescheidItem[0]}
            </EATreeItem>
        );
    }

    // eslint-disable-next-line
    function buildBescheidAntragTree(parent, parentUrl) {
        const nodeUrl = parentUrl + "/antrag";
        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={"Anträge"}>
                {!isFinalStatus(parent?.status?.id) && createBescheidAntragButton(nodeUrl)}
                {mapChildren(parent, (n) => buildBescheidNode(n, nodeUrl), "aenderungsantraege")}
            </EATreeItem>
        );
    }

    function buildBescheidNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/" + innerTree.id;
        return (
            <EATreeItem key={nodeUrl} nodeId={nodeUrl} label={buildBescheidTitle(innerTree)}>
                {buildNavigateToTreeItem("Bescheidsdaten", nodeUrl + routes.BETRIEBSAKTE_BESCHEIDDATEN)}
                {buildNavigateToTreeItem("Dateien", nodeUrl + routes.BETRIEBSAKTE_DATEIEN)}
                {mapChildren(innerTree, (n) => buildBescheidMessstelleNode(n, nodeUrl), "messstellen")}
            </EATreeItem>
        );
    }

    function buildBescheidMessstelleNode(innerTree, parentUrl) {
        const nodeUrl = parentUrl + "/messstellen/" + innerTree.id;
        return buildNavigateToTreeItem(innerTree.title, nodeUrl);
    }

    function mapChildren(node, parseFunc, childrenAttribute = null) {
        if (!node) {
            return null;
        }

        const arr = childrenAttribute ? node[childrenAttribute] : node;
        return arr && Array.isArray(arr) ? arr.map(parseFunc) : null;
    }

    function hasChildren(node, childrenAttributeName = null) {
        const childrenArr = childrenAttributeName ? node[childrenAttributeName] : node;
        return childrenArr && childrenArr.length > 0;
    }

    function buildNavigateToTreeItem(label, url) {
        const opts = {};
        if (url) {
            opts.onLabelClick = () => navigateTo(url + "?modus=l");
        }

        return <EATreeItem key={url} nodeId={url} label={label} {...opts} />;
    }

    function buildCreateUrl(url, params, createPart = "/neu/anlagendaten") {
        return url + createPart + (params ? "?" + params + (modus ? "&modus=s" : "") : "" + (modus ? "?modus=s" : ""));
    }

    function navigateTo(url) {
        if (location.pathname !== url) {
            if (location.pathname.includes("/neu/") || location.search.includes("modus=s")) {
                confirm({
                    title: "Änderungen verwerfen",
                    description: "Durch das Wegnavigieren gehen womöglich ungespeicherte Änderungen verloren!",
                    onSubmit: () => history.push(url),
                    onClose: () => {
                    },
                });
            } else {
                history.push(url);
            }
        }
    }

    const navToggleClasses = "navToggleButton" + (navOpen ? "" : " navToggleClosed");
    return (
        <div id={id}>
            <EAButtonBar>
                {!loading && !entlastungDataLoading &&
                    !isFinalStatus(treeNav?.status?.id) &&
                    betriebstyp &&
                    navOpen &&
                    userHasAnyRole([ZUST_SBD, ZUST_SBB, ZUST_SBS, FACHADMIN, SYSTEMADMIN, BETREIBER, BEAUFTRAGTER]) && (

                        <FormControlLabel
                            style={{padding: "9px"}}
                            control={
                                <div
                                    title={treeNav?.freigabestatus !== "FREIGEGEBEN" ? "Anlegen nicht möglich. Der Betrieb ist noch nicht freigegeben." : null}>
                                    <Switch
                                        id="activate-edit-mode"
                                        data-cy="activate-edit-mode"
                                        size="small"
                                        checked={isCreateAllowed}
                                        onChange={(e) => setIsCreateAllowed(e.target.checked)}
                                        disabled={treeNav?.freigabestatus !== "FREIGEGEBEN"}
                                    />
                                </div>
                            }
                            disabled={treeNav?.freigabestatus !== "FREIGEGEBEN"}
                            label={"Anlegen"}
                        />
                    )}


                <span onClick={() => setNavOpen(!navOpen)} className={navToggleClasses}/>
            </EAButtonBar>
            <EASlide id={"BetriebsakteSlide"} open={navOpen}>
                {loading || !betriebstyp ? (
                    <div style={{height: "100%", display: "flex", justifyContent: "center", alignItems: "center"}}>
                        <CircularProgress/>
                    </div>
                ) : (
                    <EATree>{buildTreeNavigation()}</EATree>
                )}
            </EASlide>
        </div>
    );
}

export default BetriebsakteNavigation;
